import { WebApiMemberDtoMemberDto, WebApiModelsMemberField } from "../Repository/eventbookyapi";
import { MemberFieldTypes } from "../Types/MemberFieldTypes";
import { DuplicateValuePolicy } from "../Types/DuplicateValuePolicy";
import { mapDynamicInputLabel } from "../Mappers/mapDynamicInputLabel";
import { mapDynamicInputName } from "../Mappers/mapDynamicName";

export interface ValidationError {
    fieldName: string;
    message: string;
}

export interface ValidationWarning {
    fieldName: string;
    message: string;
    duplicateValuePolicy: DuplicateValuePolicy;
}

export interface MemberValidationResult {
    errors: ValidationError[];
    warnings: ValidationWarning[];
    hasErrors: boolean;
    hasWarnings: boolean;
}

/**
 * Validates member data for duplicates based on field settings
 * @param values Form values to validate
 * @param allMembers All members to check against
 * @param memberFieldSettings Field settings with duplicate policies
 * @param currentMemberId Optional - ID of member being edited (to exclude from checks)
 * @param originalMember Optional - Original member data (used to check if values have changed)
 * @returns ValidationResult with errors and warnings
 */
export const validateMemberDuplicates = (
    values: any,
    allMembers: WebApiMemberDtoMemberDto[],
    memberFieldSettings: WebApiModelsMemberField[],
    currentMemberId?: number,
    originalMember?: WebApiMemberDtoMemberDto
): MemberValidationResult => {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Track duplicates for each field to show all matching members
    const fieldDuplicates = new Map<string, { members: WebApiMemberDtoMemberDto[], field: WebApiModelsMemberField, policy: number }>();

    // Check each member for duplicates
    allMembers.forEach((member: WebApiMemberDtoMemberDto) => {
        // Skip the current member if editing
        if (currentMemberId && member.memberId === currentMemberId) {
            return;
        }

        // Check uniqueId (always prevent duplicates, case-insensitive)
        if (values.uniqueId && member.uniqueId &&
            values.uniqueId.toLowerCase() === member.uniqueId.toLowerCase()) {
            errors.push({
                fieldName: "uniqueId",
                message: "Finns redan en medlem med id " + member.uniqueId
            });
        }

        // Check email (always prevent duplicates, case-insensitive)
        if (values.email?.length > 0 && member.email &&
            values.email.toLowerCase() === member.email.toLowerCase()) {
            errors.push({
                fieldName: "email",
                message: "Finns redan en medlem med epost " + member.email
            });
        }

        // Check fields with duplicate policies
        memberFieldSettings.forEach((field) => {
            const policy = field.duplicateValuePolicy as number | undefined;
            if (!policy || policy === DuplicateValuePolicy.Unspecified) {
                return;
            }

            // Get the value from form and member based on field type
            const fieldValue = getFieldValue(values, field.columnType);
            const memberFieldValue = getFieldValue(member, field.columnType);

            if (!fieldValue || !memberFieldValue || fieldValue.toLowerCase() !== memberFieldValue.toLowerCase()) {
                return;
            }

            // Check if the value has actually changed from the original
            if (originalMember) {
                const originalValue = getFieldValue(originalMember, field.columnType);
                if (originalValue && fieldValue.toLowerCase() === originalValue.toLowerCase()) {
                    // Value hasn't changed, skip validation
                    return;
                }
            }

            // Group duplicates by field
            const fieldKey = `${field.columnType}_${policy}`;
            if (!fieldDuplicates.has(fieldKey)) {
                fieldDuplicates.set(fieldKey, { members: [], field, policy });
            }
            fieldDuplicates.get(fieldKey)!.members.push(member);
        });
    });

    // Create error/warning messages for each field with all duplicate members
    fieldDuplicates.forEach(({ members, field, policy }) => {
        const fieldLabel = mapDynamicInputLabel(field.columnType!, memberFieldSettings, field.id);
        const fieldValue = getFieldValue(values, field.columnType);

        const memberList = members
            .map(m => `${m.uniqueId} (${m.firstName} ${m.lastName})`)
            .join(', ');

        const duplicateMessage = `Finns redan ${members.length} medlem${members.length > 1 ? 'mar' : ''} med ${fieldLabel} ${fieldValue}: ${memberList}`;

        if (policy === DuplicateValuePolicy.PreventDuplicates) {
            errors.push({
                fieldName: mapDynamicInputName(field),
                message: duplicateMessage
            });
        } else if (policy === DuplicateValuePolicy.AllowDuplicatesWithWarning) {
            warnings.push({
                fieldName: mapDynamicInputName(field),
                message: duplicateMessage,
                duplicateValuePolicy: policy
            });
        }
    });

    return {
        errors,
        warnings,
        hasErrors: errors.length > 0,
        hasWarnings: warnings.length > 0
    };
};

/**
 * Gets the field value from member object based on field type
 */
const getFieldValue = (memberOrValues: any, columnType?: number): string | undefined => {
    if (!columnType) return undefined;

    switch (columnType) {
        case MemberFieldTypes.UniqueId:
            return memberOrValues.uniqueId;
        case MemberFieldTypes.Email:
            return memberOrValues.email;
        case MemberFieldTypes.Email2:
            return memberOrValues.email2;
        case MemberFieldTypes.PhoneNumber:
            return memberOrValues.phoneNumber;
        case MemberFieldTypes.PhoneNumber2:
            return memberOrValues.phoneNumber2;
        case MemberFieldTypes.Extra1:
            return memberOrValues.extra1;
        case MemberFieldTypes.Extra2:
            return memberOrValues.extra2;
        case MemberFieldTypes.Extra3:
            return memberOrValues.extra3;
        case MemberFieldTypes.FirstName:
            return memberOrValues.firstName;
        case MemberFieldTypes.LastName:
            return memberOrValues.lastName;
        case MemberFieldTypes.Address:
            return memberOrValues.address;
        case MemberFieldTypes.PostalCode:
            return memberOrValues.postalCode;
        case MemberFieldTypes.PostalArea:
            return memberOrValues.postalArea;
        case MemberFieldTypes.Company:
            return memberOrValues.company;
        case MemberFieldTypes.Country:
            return memberOrValues.country;
        case MemberFieldTypes.Title:
            return memberOrValues.title;
        case MemberFieldTypes.PersonalNumber:
            return memberOrValues.personalNumber;
        case MemberFieldTypes.AdressRow2:
            return memberOrValues.adressRow2;
        case MemberFieldTypes.AdressRow3:
            return memberOrValues.adressRow3;
        case MemberFieldTypes.WebPage:
            return memberOrValues.webPage;
        default:
            return undefined;
    }
};
